import React, { useState, useEffect, useMemo } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import {
  normalizeAgentReportProperties,
  revertNormalizeDate,
} from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import DatePicker from "../../components/Forms/DatePicker/DatePicker.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import AgentReportDataGrid from "../../components/DataTables/AgentReportDataGrid.tsx";
import moment from "moment/moment";
import agentsService from "../../services/AgentsService/AgentsService.ts";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";
import { AgentReportMappingEnum } from "../../enum/normalization-mapping.enum.ts";

const AgentReport: React.FC = () => {
  const [formState, setFormState] = useState({
    fromDate: "",
    toDate: "",
    company: "",
    policyType: "",
    agent: "",
  });

  const [reportState, setReportState] = useState({
    reportNumber: "",
    reportDate: moment().format("DD/MM/YYYY"),
  });

  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [agentOptions, setAgentOptions] = useState<{ [key: string]: string }>(
    {}
  );
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [policies, setPolicies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState<[string, number][]>(
    []
  );

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [agents, companies, policyTypesRes] = await Promise.all([
          InsuranceService.getAllAgents(jwt),
          InsuranceService.getAllInsuranceCompanies(jwt),
          InsuranceService.getAllPolicyTypes(jwt),
        ]);
        setAgentOptions(agents as { [key: string]: string });
        setInsuranceCompanies(companies as { [key: string]: string });
        setPolicyTypes(policyTypesRes as { [key: string]: string });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  useEffect(() => {
    if (!formState.agent) return;
    const getAgentReportNumber = async () => {
      setReportState((prev) => ({ ...prev, reportNumber: null }));
      const agentReportId = await agentsService.getAgentReportIdByAgentId(
        jwt,
        formState.agent
      );
      if (agentReportId)
        setReportState((prev) => ({
          ...prev,
          reportNumber: agentReportId as string,
        }));
    };
    getAgentReportNumber();
  }, [formState.agent]);

  const selectedPoliciesData = useMemo(() => {
    return policies.filter((policy) =>
      selectedPolicies.some(
        ([polId, payNum]) =>
          polId === policy[AgentReportMappingEnum.Pol_ID] &&
          payNum === policy[AgentReportMappingEnum.Pay_Num]
      )
    );
  }, [selectedPolicies, policies]);

  const agentCommissionSum = useMemo(() => {
    return selectedPoliciesData.reduce((acc, row) => {
      const val = Number(row?.commissionAmount ?? 0);
      return acc + (isNaN(val) ? 0 : val);
    }, 0);
  }, [selectedPoliciesData]);

  const formatCurrency = (value: number) =>
    value.toLocaleString("bg-BG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleReportInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setReportState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const { fromDate, agent } = formState;
    if (!fromDate) {
      setAlert("Моля, въведете стартова дата");
      setAlertType(FadeAlertType.error);
      return;
    }
    if (!agent) {
      setAlert("Моля, изберете агент");
      setAlertType(FadeAlertType.error);
      return;
    }
    try {
      setIsLoading(true);
      setPolicies([]);
      const result = await PolicyService.fetchAgentReport(
        {
          ...formState,
          fromDate: revertNormalizeDate(formState.fromDate),
          toDate: revertNormalizeDate(formState.toDate),
        },
        jwt
      );
      const normalizedPolicies = Object.values(result)
        .filter((policy) => typeof policy === "object")
        .map((policy) => normalizeAgentReportProperties(policy));
      setPolicies(normalizedPolicies);
    } catch (error) {
      setAlert("Грешка при зареждането на полици.");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
    setAlert(null);
  };

  const handleSendReport = async () => {
    if (!reportState.reportNumber) {
      setAlert("Моля, въведете номер на отчета");
      setAlertType(FadeAlertType.error);
      return;
    }
    try {
      setIsLoading(true);
      await PolicyService.saveAgentReport(
        {
          agent: formState.agent,
          policies: selectedPolicies,
          reportNumber: reportState.reportNumber,
          reportDate: revertNormalizeDate(reportState.reportDate),
        },
        jwt
      );
      setAlert("Отчетът е изпратен успешно!");
      setAlertType(FadeAlertType.success);
    } catch (error) {
      setAlert("Грешка при изпращане на отчета.");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb pageName="Отчет на агент" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Отчет на агент" />

        <form onSubmit={handleSearch} className="p-3">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <div>
              <DatePicker
                label="От дата"
                name="fromDate"
                onChange={handleInputChange}
                value={formState.fromDate}
              />
            </div>
            <div>
              <DatePicker
                label="До дата"
                name="toDate"
                onChange={handleInputChange}
                value={formState.toDate}
              />
            </div>
            <div>
              <label className={labelClass}>Агент</label>
              <SelectGroup
                value={formState.agent}
                placeholder="Изберете агент"
                options={agentOptions}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, agent: value }))
                }
                name="agent"
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.company}
                placeholder="Изберете компания"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, company: value }))
                }
                name="company"
              />
            </div>
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.policyType}
                placeholder="Изберете вид полица"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, policyType: value }))
                }
                name="policyType"
              />
            </div>
          </div>

          <div className="mt-2 flex justify-center">
            <button
              type="submit"
              className="bg-primary w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Покажи
            </button>
          </div>
        </form>

        <AgentReportDataGrid
          policies={policies}
          isLoading={isLoading}
          selectedPolicies={selectedPolicies}
          setSelectedPolicies={setSelectedPolicies}
          formState={formState}
          reportState={reportState}
          labelMaps={{
            agents: agentOptions,
            companies: insuranceCompanies,
            policyTypes: policyTypes,
          }}
        />

        {policies.length > 0 && (
          <div className="border-stroke dark:border-strokedark border-t p-3">
            <div className="grid grid-cols-2 items-end gap-2 lg:grid-cols-4">
              <div>
                <label className={labelClass}>Комисион за агенти</label>
                <div className="border-stroke bg-gray-2 dark:border-strokedark dark:bg-meta-4 flex h-9 items-center rounded-sm border px-3 text-sm font-bold text-black dark:text-white">
                  {formatCurrency(agentCommissionSum)}
                </div>
              </div>
              <div>
                <label className={labelClass}>Номер на отчет</label>
                <input
                  type="text"
                  id="reportNumber"
                  name="reportNumber"
                  value={reportState.reportNumber}
                  onChange={handleReportInputChange}
                  placeholder="Въведете номер"
                  className={inputClass}
                />
              </div>
              <div>
                <DatePicker
                  label="Дата на отчет"
                  onChange={handleReportInputChange}
                  value={reportState.reportDate}
                  name="reportDate"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleSendReport}
                  disabled={isLoading}
                  className="bg-primary w-full justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90 disabled:opacity-70"
                >
                  {isLoading ? "Изпращане..." : "Изпрати"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AgentReport;
